import { Dialog } from "../../ui/layers";
import { useManagedSigningExperienceCopy } from "./managedSigningExperienceCatalog";
import { useEffect, useMemo, useState } from "react";
import {
  CheckCircle2,
  Fingerprint,
  KeyRound,
  LockKeyhole,
  Plus,
  RefreshCcw,
  SearchCheck,
  ShieldCheck,
  Trash2,
} from "lucide-react";

import { useKeysTrustManagedCopy, type KeysTrustManagedMessageKey } from "./keysTrustManagedCatalog";
import {
  abandonDuplicateStagedTufIdentity,
  activateTufSigningIdentity,
  getTufActivationPreflight,
  listManagedSigningIdentities,
  listTufActivations,
  stageManagedSigningIdentity,
  type ManagedSigningIdentity,
  type TufActivation,
  type TufActivationChannel,
  type TufActivationPreflight,
  type TufManagedSigningPurpose,
} from "./keysTrustApi";
import "../../styles/keys-trust-managed.css";

const CHANNEL: TufActivationChannel = "beta";

const PURPOSES: readonly TufManagedSigningPurpose[] = [
  "tuf_targets_signer",
  "tuf_snapshot_signer",
  "tuf_timestamp_signer",
];

const PURPOSE_LABELS: Record<TufManagedSigningPurpose, string> = {
  tuf_targets_signer: "Targets",
  tuf_snapshot_signer: "Snapshot",
  tuf_timestamp_signer: "Timestamp",
};

type KeysTrustTranslate = ReturnType<typeof useKeysTrustManagedCopy>["t"];

const PURPOSE_DESCRIPTION_KEYS: Record<TufManagedSigningPurpose, KeysTrustManagedMessageKey> = {
  tuf_targets_signer: "tufTargetsDescription",
  tuf_snapshot_signer: "tufSnapshotDescription",
  tuf_timestamp_signer: "tufTimestampDescription",
};

function isTufPurpose(value: ManagedSigningIdentity["purpose"]): value is TufManagedSigningPurpose {
  return value !== "collector_release_signer";
}

function isTufIdentity(
  value: ManagedSigningIdentity,
): value is ManagedSigningIdentity & { purpose: TufManagedSigningPurpose } {
  return isTufPurpose(value.purpose);
}

function storageKey(purpose: TufManagedSigningPurpose): string {
  return `kavoosh.keysTrust.stage.${purpose}`;
}

function operationId(purpose: TufManagedSigningPurpose): string {
  const key = storageKey(purpose);
  const existing = window.sessionStorage.getItem(key);
  if (existing) return existing;
  const next = window.crypto.randomUUID();
  window.sessionStorage.setItem(key, next);
  return next;
}

function shortDigest(value: string): string {
  return `${value.slice(0, 12)}…${value.slice(-12)}`;
}

function activationByPurpose(
  activations: Record<string, TufActivation>,
  purpose: TufManagedSigningPurpose,
): TufActivation | undefined {
  return Object.values(activations).find(
    (activation) => activation.purpose === purpose && activation.channel === CHANNEL,
  );
}

function prerequisiteCopy(
  purpose: TufManagedSigningPurpose,
  t: KeysTrustTranslate,
): string {
  if (purpose === "tuf_snapshot_signer") return t("tufSnapshotPrerequisite");
  if (purpose === "tuf_timestamp_signer") return t("tufTimestampPrerequisite");
  return "";
}

export function TufSigningPreparationPanel({
  onSessionUnavailable,
}: {
  onSessionUnavailable?: () => void;
}) {
  const { t } = useKeysTrustManagedCopy();
  const c=useManagedSigningExperienceCopy();
  const [confirmation,setConfirmation]=useState<{action:"activate"|"discard";item:ManagedSigningIdentity & {purpose:TufManagedSigningPurpose}}|null>(null);
  const [items, setItems] = useState<ManagedSigningIdentity[]>([]);
  const [setupPurpose, setSetupPurpose] = useState<TufManagedSigningPurpose>("tuf_targets_signer");
  const [preflights, setPreflights] = useState<Record<string, TufActivationPreflight>>({});
  const [activations, setActivations] = useState<Record<string, TufActivation>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [checkingId, setCheckingId] = useState<string | null>(null);
  const [activatingId, setActivatingId] = useState<string | null>(null);
  const [discardingId, setDiscardingId] = useState<string | null>(null);
  const [restartCheckpointRequired, setRestartCheckpointRequired] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [identities, active] = await Promise.all([
        listManagedSigningIdentities(),
        listTufActivations(),
      ]);
      setItems(
        identities
          .filter(isTufIdentity)
          .filter((item) => item.lifecycle !== "abandoned"),
      );
      setActivations(
        Object.fromEntries(active.map((activation) => [activation.operation_id, activation])),
      );
      setPreflights({});
    } catch (reason) {
      const detail = reason instanceof Error ? reason.message : t("tufLoadManagedError");
      if (detail === "SESSION_UNAVAILABLE") onSessionUnavailable?.();
      else setError(detail);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, [t]);

  const staged = useMemo(() => items.filter((item) => item.lifecycle === "staged"), [items]);
  const duplicateIds = useMemo(() => {
    const values = new Set<string>();
    for (const purpose of PURPOSES) {
      const matching = staged
        .filter((item) => item.purpose === purpose)
        .sort((left, right) => {
          const created = left.created_at.localeCompare(right.created_at);
          return created || left.operation_id.localeCompare(right.operation_id);
        });
      for (const duplicate of matching.slice(1)) values.add(duplicate.operation_id);
    }
    return values;
  }, [staged]);

  const nextPurpose = PURPOSES.find((purpose) => !activationByPurpose(activations, purpose)) ?? null;
  const currentPurpose = restartCheckpointRequired ? null : nextPurpose;
  const activeCount = PURPOSES.filter((purpose) => activationByPurpose(activations, purpose)).length;
  const setupRoleExists = staged.some((item) => item.purpose === setupPurpose);

  function keeperForPurpose(
    purpose: TufManagedSigningPurpose,
  ): (ManagedSigningIdentity & { purpose: TufManagedSigningPurpose }) | undefined {
    return staged
      .filter((item): item is ManagedSigningIdentity & { purpose: TufManagedSigningPurpose } => (
        item.purpose === purpose && isTufIdentity(item)
      ))
      .sort((left, right) => {
        const created = left.created_at.localeCompare(right.created_at);
        return created || left.operation_id.localeCompare(right.operation_id);
      })[0];
  }

  async function generate() {
    if (setupRoleExists || restartCheckpointRequired) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    const id = operationId(setupPurpose);
    try {
      const created = await stageManagedSigningIdentity({ operationId: id, purpose: setupPurpose });
      if (!isTufIdentity(created)) throw new Error(t("tufStageWrongPurpose"));
      window.sessionStorage.removeItem(storageKey(setupPurpose));
      setItems((current) => [created, ...current.filter((item) => item.operation_id !== created.operation_id)]);
      setMessage(t("tufSignerStaged", { role: PURPOSE_LABELS[setupPurpose] }));
    } catch (reason) {
      const detail = reason instanceof Error ? reason.message : t("tufStageError");
      if (detail === "SESSION_UNAVAILABLE") onSessionUnavailable?.();
      else setError(detail);
    } finally {
      setBusy(false);
    }
  }

  async function discardDuplicate(
    item: ManagedSigningIdentity & { purpose: TufManagedSigningPurpose },
  ) {
    if (!duplicateIds.has(item.operation_id) || restartCheckpointRequired) return;
    setDiscardingId(item.operation_id);
    setError(null);
    setMessage(null);
    try {
      const abandoned = await abandonDuplicateStagedTufIdentity(item.operation_id);
      if (abandoned.purpose !== item.purpose) {
        throw new Error(t("tufDiscardWrongPurpose"));
      }
      setItems((current) => current.filter((value) => value.operation_id !== item.operation_id));
      setPreflights((current) => {
        const next = { ...current };
        delete next[item.operation_id];
        return next;
      });
      setMessage(t("tufDuplicateDiscarded", { role: PURPOSE_LABELS[item.purpose] }));
    } catch (reason) {
      const detail = reason instanceof Error ? reason.message : t("tufDiscardError");
      if (detail === "SESSION_UNAVAILABLE") onSessionUnavailable?.();
      else setError(detail);
    } finally {
      setDiscardingId(null);
    }
  }

  async function preflight(item: ManagedSigningIdentity & { purpose: TufManagedSigningPurpose }) {
    if (restartCheckpointRequired || item.lifecycle !== "staged" || item.purpose !== currentPurpose) return;
    setCheckingId(item.operation_id);
    setError(null);
    setMessage(null);
    try {
      const value = await getTufActivationPreflight(item.operation_id, CHANNEL);
      if (value.purpose !== item.purpose || value.key_id !== item.key_id || value.channel !== CHANNEL) {
        throw new Error(t("tufPreflightMismatch"));
      }
      setPreflights((current) => ({ ...current, [item.operation_id]: value }));
    } catch (reason) {
      const detail = reason instanceof Error ? reason.message : t("tufPreflightError");
      if (detail === "SESSION_UNAVAILABLE") onSessionUnavailable?.();
      else setError(detail);
    } finally {
      setCheckingId(null);
    }
  }

  async function activate(item: ManagedSigningIdentity & { purpose: TufManagedSigningPurpose }) {
    const value = preflights[item.operation_id];
    if (restartCheckpointRequired || !value || value.channel !== CHANNEL || item.purpose !== currentPurpose) return;
    setActivatingId(item.operation_id);
    setError(null);
    setMessage(null);
    try {
      const activation = await activateTufSigningIdentity(item.operation_id, CHANNEL);
      if (
        activation.purpose !== item.purpose
        || activation.key_id !== item.key_id
        || activation.root_version !== value.root_version
        || activation.threshold !== value.threshold
        || activation.channel !== CHANNEL
      ) {
        throw new Error(t("tufActivationMismatch"));
      }
      setActivations((current) => ({ ...current, [item.operation_id]: activation }));
      setPreflights((current) => {
        const next = { ...current };
        delete next[item.operation_id];
        return next;
      });
      setRestartCheckpointRequired(true);
      setMessage(t("tufActivatedMessage", { role: PURPOSE_LABELS[item.purpose] }));
    } catch (reason) {
      const detail = reason instanceof Error ? reason.message : t("tufActivationError");
      if (detail === "SESSION_UNAVAILABLE") onSessionUnavailable?.();
      else setError(detail);
    } finally {
      setActivatingId(null);
    }
  }

  const actionBusy = busy || checkingId !== null || activatingId !== null || discardingId !== null;

  return <section className="keys-trust-managed keys-trust-tuf-flow" aria-labelledby="managed-tuf-preparation-title">
    <header>
      <div>
        <p className="admin-v12-eyebrow">{t("tufActivationEyebrow")}</p>
        <h2 id="managed-tuf-preparation-title">{t("tufActivationTitle")}</h2>
        <p>{t("tufActivationBody")}</p>
      </div>
      <button type="button" className="admin-v12-secondary" onClick={() => void load()} disabled={loading || actionBusy}>
        <RefreshCcw size={15} />{loading ? t("loading") : t("refresh")}
      </button>
    </header>

    <div className="tuf-flow-scope" role="note">
      <ShieldCheck size={17} />
      <div>
        <strong>{t("tufFixedCheckpointTitle")}</strong>
        <span>{t("tufFixedCheckpointBody")}</span>
      </div>
      <b>{t("tufActiveCount", { count: activeCount })}</b>
    </div>

    {restartCheckpointRequired && <div className="tuf-restart-barrier" role="alert">
      <LockKeyhole size={18} />
      <div>
        <strong>{t("tufRestartTitle")}</strong>
        <span>{t("tufRestartBody")}</span>
      </div>
    </div>}

    <ol className="tuf-activation-steps" aria-label={t("tufActivationOrderAria")}>
      {PURPOSES.map((purpose, index) => {
        const activation = activationByPurpose(activations, purpose);
        const isCurrent = currentPurpose === purpose;
        return <li
          key={purpose}
          className={activation ? "is-complete" : isCurrent ? "is-current" : "is-locked"}
          aria-current={isCurrent ? "step" : undefined}
        >
          <span>{activation ? <CheckCircle2 size={16} /> : isCurrent ? index + 1 : <LockKeyhole size={15} />}</span>
          <div><strong>{PURPOSE_LABELS[purpose]}</strong><small>{activation ? t("active") : isCurrent ? t("currentStep") : t("locked")}</small></div>
        </li>;
      })}
    </ol>

    {error && <p className="admin-v12-form-error" role="alert">{error}</p>}
    {message && <p className="keys-trust-managed-success" role="status">{message}</p>}

    <div className="tuf-role-workflow">
      {PURPOSES.map((purpose, index) => {
        const item = keeperForPurpose(purpose);
        const activation = activationByPurpose(activations, purpose);
        const isCurrent = currentPurpose === purpose;
        const duplicates = staged.filter((candidate) => (
          candidate.purpose === purpose && duplicateIds.has(candidate.operation_id)
        ));
        const value = item ? preflights[item.operation_id] : undefined;
        const verified = Boolean(value && value.channel === CHANNEL && !activation);
        const state = activation
          ? t("tufStateActive")
          : isCurrent && duplicates.length > 0
            ? t("tufStateRecoveryRequired")
            : isCurrent && !item
              ? t("tufStateSignerMissing")
              : isCurrent
                ? t("tufStateCurrent")
                : t("tufStateLocked");

        return <article
          key={purpose}
          className={`tuf-role-step ${isCurrent ? "is-current" : ""} ${activation ? "is-complete" : ""}`}
          aria-current={isCurrent ? "step" : undefined}
        >
          <header>
            <div className="tuf-role-heading">
              <span className="tuf-step-number">{index + 1}</span>
              <div>
                <strong>{PURPOSE_LABELS[purpose]}</strong>
                <small>{t(PURPOSE_DESCRIPTION_KEYS[purpose])}</small>
              </div>
            </div>
            <b className={`keys-trust-managed-state ${activation ? "state-active" : isCurrent ? "state-staged" : ""}`}>{state}</b>
          </header>

          {item && <div className="tuf-role-summary">
            <Fingerprint size={15} />
            <span><strong>{t("tufKeySummary", { key: shortDigest(item.key_id ?? item.operation_id) })}</strong><small>{t("tufProtectedLocalCustody")}</small></span>
          </div>}

          {activation && <div className="tuf-role-result is-success" aria-live="polite">
            <CheckCircle2 size={18} />
            <div>
              <strong>{t("tufActivatedUnderRoot", { version: activation.root_version })}</strong>
              <span>{t("tufActivatedDetail", { threshold: activation.threshold })}</span>
            </div>
          </div>}

          {!activation && !isCurrent && <div className="tuf-role-result is-locked">
            <LockKeyhole size={17} />
            <div><strong>{t("tufNotAvailableYet")}</strong><span>{restartCheckpointRequired && purpose === nextPurpose ? t("tufRestartBeforeStep") : prerequisiteCopy(purpose, t)}</span></div>
          </div>}

          {!activation && isCurrent && duplicates.length > 0 && <div className="tuf-role-result is-warning">
            <Trash2 size={17} />
            <div>
              <strong>{t("tufResolveDuplicateTitle")}</strong>
              <span>{t("tufResolveDuplicateBody")}</span>
              {duplicates.map((duplicate) => <button
                key={duplicate.operation_id}
                type="button"
                className="admin-v12-secondary"
                aria-label={t("tufDiscardDuplicateAria", { role: PURPOSE_LABELS[purpose] })}
                onClick={() => setConfirmation({action:"discard",item:duplicate as ManagedSigningIdentity & { purpose: TufManagedSigningPurpose }})}
                disabled={actionBusy || restartCheckpointRequired}
              >
                <Trash2 size={14} />{discardingId === duplicate.operation_id ? t("tufDiscarding") : t("tufDiscardButton", { key: shortDigest(duplicate.key_id ?? duplicate.operation_id) })}
              </button>)}
            </div>
          </div>}

          {!activation && isCurrent && duplicates.length === 0 && !item && <div className="tuf-role-result is-warning">
            <KeyRound size={17} />
            <div><strong>{t("tufNoStagedSigner", { role: PURPOSE_LABELS[purpose] })}</strong><span>{t("tufNoStagedSignerBody")}</span></div>
          </div>}

          {!activation && isCurrent && duplicates.length === 0 && item && !verified && <div className="tuf-current-action">
            <div>
              <strong>{t("tufVerifyAuthorityTitle")}</strong>
              <span>{t("tufVerifyAuthorityBody")}</span>
            </div>
            <button
              type="button"
              className="admin-v12-primary"
              aria-label={t("tufVerifyAuthorityAria", { role: PURPOSE_LABELS[purpose] })}
              onClick={() => void preflight(item)}
              disabled={actionBusy || restartCheckpointRequired}
            >
              <SearchCheck size={15} />{checkingId === item.operation_id ? t("tufVerifying") : t("tufVerifyAuthorityButton", { role: PURPOSE_LABELS[purpose] })}
            </button>
          </div>}

          {!activation && isCurrent && duplicates.length === 0 && item && verified && value && <div className="tuf-current-action is-verified" aria-live="polite">
            <div>
              <strong><CheckCircle2 size={16} /> {t("tufAuthorityVerified")}</strong>
              <span>{t("tufVerifiedDetail", { version: value.root_version, threshold: value.threshold, role: value.role })}</span>
            </div>
            <button
              type="button"
              className="admin-v12-primary"
              aria-label={t("tufActivateAria", { role: PURPOSE_LABELS[purpose] })}
              onClick={() => setConfirmation({action:"activate",item})}
              disabled={actionBusy || restartCheckpointRequired}
            >
              <ShieldCheck size={15} />{activatingId === item.operation_id ? t("tufActivating") : t("tufActivateButton", { role: PURPOSE_LABELS[purpose] })}
            </button>
          </div>}

          {item && <details className="tuf-technical-details">
            <summary>{t("tufTechnicalDetails")}</summary>
            <dl>
              <div><dt>{t("keyId")}</dt><dd><code>{item.key_id ?? t("pending")}</code></dd></div>
              <div><dt>{t("fingerprintLabel")}</dt><dd><code>{item.fingerprint_sha256 ?? t("pending")}</code></dd></div>
              <div><dt>{t("provider")}</dt><dd>{item.provider_kind.replaceAll("_", " ")}</dd></div>
              <div><dt>{t("version")}</dt><dd>{item.version}</dd></div>
            </dl>
          </details>}
        </article>;
      })}
    </div>

    <details className="tuf-signer-setup">
      <summary>{t("tufSignerSetup")}</summary>
      <div>
        <label>{t("tufSignerSetupRole")}
          <select value={setupPurpose} onChange={(event) => setSetupPurpose(event.currentTarget.value as TufManagedSigningPurpose)} disabled={actionBusy || restartCheckpointRequired}>
            {PURPOSES.map((purpose) => <option key={purpose} value={purpose}>{PURPOSE_LABELS[purpose]}</option>)}
          </select>
        </label>
        <button type="button" className="admin-v12-secondary" onClick={() => void generate()} disabled={actionBusy || restartCheckpointRequired || setupRoleExists}>
          <Plus size={15} />{busy ? t("working") : setupRoleExists ? t("tufStagedSignerExists") : t("tufGenerateMissingSigner")}
        </button>
        <p>{t("tufSignerSetupBody")}</p>
      </div>
    </details>

    <p className="keys-trust-provider-note">{t("tufProviderNote")}</p>
    <Dialog open={confirmation!==null} onDismiss={()=>setConfirmation(null)} dismissible={!actionBusy} title={confirmation?.action === "discard" ? t("tufResolveDuplicateTitle") : t("tufActivationTitle")} description={confirmation ? confirmation.action === "discard" ? t("tufDiscardConfirm",{role:PURPOSE_LABELS[confirmation.item.purpose],key:shortDigest(confirmation.item.key_id??confirmation.item.operation_id)}) : t("tufActivateConfirm",{role:PURPOSE_LABELS[confirmation.item.purpose],version:preflights[confirmation.item.operation_id]?.root_version??0}) : ""} footer={<><button type="button" className="admin-v12-secondary" disabled={actionBusy} onClick={()=>setConfirmation(null)}>{c("cancel")}</button><button type="button" className="admin-v12-primary" disabled={actionBusy} onClick={()=>void (async()=>{if(!confirmation)return;if(confirmation.action==="discard")await discardDuplicate(confirmation.item);else await activate(confirmation.item);setConfirmation(null);})()}>{actionBusy?t("working"):confirmation?.action==="discard"?t("tufDiscardDuplicateAria",{role:PURPOSE_LABELS[confirmation.item.purpose]}):c("activate")}</button></>}>
      <p>{t("tufActivationBody")}</p><code>{confirmation?.item.fingerprint_sha256}</code><p>{t("boundaryBody")}</p>{error && <p role="alert">{error}</p>}
    </Dialog>
  </section>;
}
